import React, { useState } from 'react';
import {
  CountryCode,
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  NumberFormat,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { hasFlag } from 'country-flag-icons';
import * as Flags from 'country-flag-icons/react/3x2';
import * as Popover from '@radix-ui/react-popover';
import { useCombobox } from 'downshift';

import { ChevronDownOutlineIcon, ChevronUpOutlineIcon, SearchFillIcon } from '@applyboard/ui-icons';

import { Box } from '../Box';
import { ComboboxOptionProps } from '../Combobox';
import { FieldCompatibleProps } from '../Field';
import { Flex } from '../Flex';
import { HelpText } from '../HelpText';
import { Text } from '../Text';

import {
  IconWrapper,
  InternalCountryFlagPlaceholder,
  InternalTextInput,
  LabelText,
  createInputColorUtil,
} from '~/internal';
import { handleFieldsetBlur, useId } from '~/utils';
import { Intent, useFullTheme } from '~/design';
import { useContextOrThrow } from '~/utils/useContextOrThrow';
import { unstable_batchedUpdates as batch } from 'react-dom';

const DEFAULT_COUNTRY: {
  label: string;
  value: CountryCode;
  leadIcon: Flags.FlagComponent;
} = {
  label: 'Canada',
  value: 'CA',
  leadIcon: Flags.CA,
};

const defaultCountry = DEFAULT_COUNTRY.value;

const countryName = new Intl.DisplayNames(['en'], { type: 'region' });
const countryOptions: ComboboxOptionProps[] = getCountries().map(country => {
  const leadIcon = hasFlag(country) ? Flags[country] : undefined;
  return {
    label: countryName.of(country) ?? country,
    value: country,
    leadIcon,
  };
});

interface PhoneInputContextProps {
  adjustedIntent: Intent;
  disabled: boolean;
  selectedCountry: CountryCode;
}

const PhoneInputContext = React.createContext<PhoneInputContextProps | null>(null);
export interface PhoneInputProps extends FieldCompatibleProps<string, HTMLInputElement> {
  /** The aria-decsribed-by of the phone input. */
  'aria-describedby'?: string;

  /** The aria-label of the phone input. */
  'aria-label'?: string;

  /** The aria-labelledby of the phone input. */
  'aria-labelledby'?: string;

  /** If true, the phone input cannot be interacted with. */
  disabled?: boolean;

  /** The intent of the phone input. Maps to color. */
  intent?: Intent;

  /** The placeholder of the phone number input. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  /** Format of the phone number returned to the client (default: 'E.164') */
  phoneNumberFormat?: 'international' | 'E.164';
}

/**
 * PhoneInput consists of a country select and a text input that, once interacted with, correctly
 * formats the provided value according to the selected country national format.
 * If considered a possible number, the value will be returned in the format
 * `+[country code][phone number with no special characters]`
 *
 * When a value is provided following the same format, the initial selected country will be set
 * according the provided number's country code.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    disabled = false,
    helpText,
    intent = 'secondary',
    invalid,
    label = '',
    onBlur,
    onChange,
    placeholder,
    required,
    value,
    phoneNumberFormat = 'E.164',
  },
  ref,
) {
  const theme = useFullTheme();
  const id = useId();
  const helpTextId = `${id}-helptext`;
  const adjustedIntent = invalid ? 'negative' : intent;
  const colorUtil = createInputColorUtil(theme);

  // Dak's note: use a different initial value for `prevValue` compared to `value` to force the
  // if statement below to run. I'm sure this can be done better but I've got brain fog right now.
  const [prevValue, setPrevValue] = useState('RANDOM_INITIAL_VALUE');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const phoneInputRef = React.useRef<HTMLInputElement | null>(ref as unknown as HTMLInputElement);

  if (prevValue !== value) {
    if (value) {
      /*
       * If the value starts with a '+' sign, we assume it's a complete phone number and parse it
       * as such. If it doesn't, we assume it's an incomplete phone number and format it as E.164
       * with what value we have.
       * ref: https://gitlab.com/catamphetamine/libphonenumber-js#parsephonenumberstring-defaultcountry-string--options-object-phonenumber
       */
      const formattedValue = value.startsWith('+')
        ? value
        : `+${getCountryCallingCode(defaultCountry)}${value}`;

      const parsedNumber = parsePhoneNumberFromString(formattedValue);
      if (parsedNumber) {
        /*
         * If the parsed number is possible, we format it as a national number for display.
         * If it's not possible, we format it as an incomplete number with the default country.
         * ref: https://gitlab.com/catamphetamine/libphonenumber-js#parsephonenumberstring-defaultcountry-string--options-object-phonenumber
         */
        const formattedPhoneNumber = parsedNumber.isPossible()
          ? parsedNumber.formatNational()
          : formatIncompletePhoneNumber(parsedNumber.nationalNumber, defaultCountry);

        batch(() => {
          setSelectedCountry(parsedNumber.country || defaultCountry);
          setPhoneNumber(formattedPhoneNumber);
        });
      }
    } else {
      batch(() => {
        setSelectedCountry(defaultCountry);
        setPhoneNumber('');
      });
    }

    setPrevValue(value);
  }

  if (!intent) {
    throw new Error('"intent" prop must be supplied.');
  }

  if (!label && !ariaLabel && !ariaLabelledby) {
    throw new Error(
      'One of `label`, `aria-label`, or `aria-labelledby` is required for accessibility reasons.',
    );
  }

  if ([label, ariaLabel, ariaLabelledby].filter(Boolean).length > 1) {
    throw new Error('Only one of `label`, `aria-label`, `aria-labelledby` can be used.');
  }

  const handleOnChange = (phoneNumber: string, selectedCountry?: CountryCode) => {
    const country = selectedCountry || defaultCountry;

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, country);

    if (parsedPhoneNumber) {
      const formattedNumber = parsedPhoneNumber.format(
        phoneNumberFormat.toUpperCase() as NumberFormat,
      );
      onChange(formattedNumber);
    } else {
      const incompleteNumber = `+${getCountryCallingCode(country)}${phoneNumber.replace(/\D/g, '')}`;
      onChange(incompleteNumber);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // https://github.com/catamphetamine/libphonenumber-js/issues/225#issuecomment-805115384
    let formattedNumber = formatIncompletePhoneNumber(inputValue, selectedCountry);
    if (inputValue.length < phoneNumber.length && formattedNumber === phoneNumber) {
      formattedNumber = formattedNumber.slice(0, -1);
    }

    batch(() => {
      setPhoneNumber(formattedNumber);
      handleOnChange(inputValue, selectedCountry);
    });
  };

  const handleCountryChange = (newCountry: CountryCode) => {
    batch(() => {
      setSelectedCountry(newCountry);
      setPhoneNumber(formatIncompletePhoneNumber(phoneNumber, newCountry));
    });

    handleOnChange(phoneNumber, newCountry);
    phoneInputRef.current?.focus();
  };

  const handleBlur = () => {
    batch(() => {
      setPhoneNumber(formatIncompletePhoneNumber(phoneNumber, selectedCountry));
      handleOnChange(phoneNumber, selectedCountry);
    });
  };

  const inputStyle = {
    backgroundColor: {
      magic: theme.colors.bgMagicHighlight,
      negative: theme.colors.bgNegativeHighlight,
      positive: theme.colors.bgPositiveHighlight,
      primary: theme.colors.bgPrimaryHighlight,
      secondary: theme.colors.bgRegular,
      warning: theme.colors.bgWarningHighlight,
    }[adjustedIntent],
    borderColor: colorUtil('border', adjustedIntent),
    borderRadius: theme.borderRadiuses.control,
    borderWidth: theme.spacing['1px'],
    color: {
      magic: theme.colors.textMagicStark,
      negative: theme.colors.textNegativeStark,
      positive: theme.colors.textPositiveStark,
      primary: theme.colors.textPrimaryStark,
      secondary: theme.colors.textSecondaryStark,
      warning: theme.colors.textWarningStark,
    }[adjustedIntent],
    paddingInline: theme.spacing[4],
    width: '100%',
  };

  const borderStyle = {
    borderColor: colorUtil('border', adjustedIntent),
    borderRadius: theme.borderRadiuses.control,
    borderStyle: 'solid',
    borderWidth: theme.spacing['1px'],
    '&:enabled:hover': {
      borderColor: colorUtil('borderHover', adjustedIntent),
      backgroundColor: {
        magic: theme.colors.bgMagicHighlightHover,
        negative: theme.colors.bgNegativeHighlightHover,
        positive: theme.colors.bgPositiveHighlightHover,
        primary: theme.colors.bgPrimaryHighlightHover,
        secondary: theme.colors.bgRegularHover,
        warning: theme.colors.bgWarningHighlightHover,
      }[adjustedIntent],
    },
    '&:enabled:focus-visible': {
      outlineWidth: theme.spacing['2px'],
      outlineStyle: 'solid',
      outlineColor: colorUtil('outline', adjustedIntent),
      borderColor: 'transparent',
    },
    '&:disabled': {
      borderColor: theme.colors.borderInactive,
      backgroundColor: theme.colors.bgInactive,
      color: theme.colors.textInactive,
      cursor: 'not-allowed',
    },
  };

  return (
    <PhoneInputContext.Provider
      value={{
        adjustedIntent,
        disabled,
        selectedCountry,
      }}
    >
      {/* This is fine to disable because we're reacting to blurs from child interactive elements */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <fieldset
        css={{
          border: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing[2],
          marginInline: 0,
          padding: 0,
          width: '100%',
        }}
        onBlur={event => handleFieldsetBlur({ event, onBlur })}
      >
        {label && (
          <label css={{ margin: 0 }} htmlFor={id} id={`label-${id}`}>
            <LabelText disabled={disabled} required={required}>
              {label}
            </LabelText>
          </label>
        )}
        <div
          css={{
            ...inputStyle,
            ...borderStyle,
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            margin: 0,
            minHeight: theme.spacing[12],
            '&:focus-within': {
              outlineWidth: theme.spacing['2px'],
              outlineStyle: 'solid',
              outlineColor: colorUtil('outline', adjustedIntent ?? 'secondary'),
              borderColor: 'transparent',
            },
          }}
        >
          <CountrySelect onBlur={handleBlur} onChange={handleCountryChange} />
          <div
            css={{
              alignSelf: 'stretch',
              borderLeftColor: colorUtil('border', adjustedIntent),
              borderLeftStyle: 'solid',
              borderWidth: theme.spacing['1px'],
              display: 'flex',
              flexGrow: 1,
              flexShrink: 1,
              justifyContent: 'center',
            }}
          >
            <div
              css={{
                alignItems: 'center',
                display: 'flex',
                width: '100%',
              }}
            >
              <InternalTextInput
                aria-describedby={helpText ? helpTextId : undefined}
                aria-label={label || ariaLabelledby ? undefined : ariaLabel ?? undefined}
                aria-labelledby={
                  label || ariaLabel ? undefined : ariaLabelledby ? ariaLabelledby : `label-${id}`
                }
                borders={false}
                disabled={disabled}
                id={id}
                intent={adjustedIntent}
                onBlur={handleBlur}
                onChange={handleInputChange}
                placeholder={placeholder}
                ref={phoneInputRef}
                required={required}
                size="sm"
                type="tel"
                value={phoneNumber}
              />
            </div>
          </div>
        </div>
        {helpText && (
          <HelpText
            id={helpTextId}
            intent={adjustedIntent}
            showIcon={invalid || disabled || adjustedIntent !== 'secondary'}
          >
            {helpText}
          </HelpText>
        )}
      </fieldset>
    </PhoneInputContext.Provider>
  );
});

interface CountrySelectProps {
  onBlur: () => void;
  onChange: (selectedCountry: CountryCode) => void;
}

const CountrySelect = React.forwardRef<HTMLButtonElement, CountrySelectProps>(
  function CountrySelect({ onBlur, onChange }, ref) {
    const [items, setItems] = React.useState(countryOptions);
    const triggerBtnRef = React.useRef<HTMLButtonElement>(null);

    const { adjustedIntent, disabled, selectedCountry } = useContextOrThrow(
      PhoneInputContext,
      '`CountrySelect` can only be rendered inside `PhoneInput`',
    );

    const theme = useFullTheme();
    const colorUtil = createInputColorUtil(theme);

    const {
      closeMenu,
      getInputProps,
      getItemProps,
      getMenuProps,
      getToggleButtonProps,
      highlightedIndex,
      isOpen,
      selectedItem,
    } = useCombobox({
      onInputValueChange({ inputValue }) {
        setItems(countryOptions.filter(filterCountries(inputValue ?? '')));
      },
      onSelectedItemChange({ selectedItem }) {
        onChange(selectedItem ? (selectedItem.value as CountryCode) : defaultCountry);
      },
      items,
      selectedItem: countryOptions.find(country => country.value === selectedCountry),
      itemToString(item) {
        return item ? item.label : '';
      },
      stateReducer: (_, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        switch (type) {
          case useCombobox.stateChangeTypes.ToggleButtonClick:
            if (!isOpen) {
              return changes;
            }

            return {
              ...changes,
              inputValue: '',
            };
        }
        return changes;
      },
    });

    const inputProps = getInputProps({ 'aria-label': 'Search for a country' });

    return (
      <Popover.Root open={isOpen}>
        <Popover.Anchor
          css={{
            flexWrap: 'nowrap',
            paddingRight: theme.spacing[4],
          }}
        >
          <Popover.Trigger
            asChild
            ref={triggerBtnRef}
            onKeyDown={event => {
              if (event.key === 'Escape' && isOpen) {
                // Stops this particular radix instance from handling the Esc keypress
                // See https://applyboard.atlassian.net/browse/UIE-1564
                closeMenu();
                event.preventDefault();
              }
            }}
          >
            <button
              {...getToggleButtonProps({
                'aria-label': 'Select a country',
                disabled,
                ref,
                tabIndex: 0,
                type: 'button',
              })}
              css={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                borderRadius: theme.borderRadiuses.control,
                borderStyle: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: theme.spacing[4],
                paddingInline: 0,
                padding: theme.spacing[1],
                '&:focus-within': {
                  borderColor: 'transparent',
                  outlineColor: colorUtil('outline', adjustedIntent),
                  outlineStyle: 'solid',
                  outlineWidth: theme.spacing['2px'],
                },
              }}
              onBlur={onBlur}
              type="button"
            >
              <CountryLabel country={selectedItem ?? DEFAULT_COUNTRY} showCountryName={false} />
              <IconWrapper contrast="high" disabled={disabled} intent="secondary" selector="button">
                {isOpen ? <ChevronUpOutlineIcon /> : <ChevronDownOutlineIcon />}
              </IconWrapper>
            </button>
          </Popover.Trigger>
        </Popover.Anchor>

        <Popover.Content
          align="start"
          aria-label="Country options"
          collisionPadding={8}
          forceMount
          asChild
          // @ts-expect-error Allow `pointerEvents: all !important`
          css={{
            zIndex: theme.zIndex.dialog.content + 1,
            // `!important` is needed because Radix is setting `pointer-events: none` in a `style`
            // attribute, which overrides the `css` prop
            pointerEvents: 'all !important',
          }}
        >
          <div
            css={{
              background: theme.colors.bgRegular,
              borderRadius: theme.borderRadiuses.control,
              boxShadow: theme.elevations.z1,
              display: isOpen ? 'flex' : 'none',
              flexDirection: 'column',
              listStyle: 'none',
              marginTop: '2px',
              maxHeight: 'var(--radix-popover-content-available-height)',
              minHeight: Math.min(countryOptions.length * 48, 100),
              minWidth: 400,
              overflow: 'hidden',
              pointerEvents: 'all',
              position: 'absolute',
              width: '100%',
              zIndex: isOpen ? 1 : undefined, //make the zIndex primary when open
            }}
          >
            <Box px={2} pt={2}>
              <InternalTextInput
                leadAdornment={<SearchFillIcon />}
                {...inputProps}
                // @ts-expect-error TODO Not sure why this is complaining as it's always there?
                onChange={inputProps.onChange}
              />
            </Box>
            <div
              css={{
                height: '100%',
                overflow: 'auto',
                '::-webkit-scrollbar': {
                  backgroundColor: 'transparent',
                  width: theme.spacing[2],
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.colors.borderRegularHover,
                  borderRadius: theme.borderRadiuses.container,
                  marginRight: theme.spacing[1],
                },
              }}
            >
              <ul
                {...getMenuProps({ 'aria-label': 'Available countries' })}
                css={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                }}
              >
                {items.map((country, index) => (
                  <li
                    key={country.value}
                    {...getItemProps({ item: country, index })}
                    css={{
                      paddingTop: theme.spacing[3],
                      paddingRight: theme.spacing[4],
                      paddingBottom: theme.spacing[3],
                      paddingLeft: theme.spacing[4],
                      background:
                        selectedItem?.value === country.value
                          ? theme.colors.bgPrimary
                          : highlightedIndex === index
                            ? theme.colors.bgRegularHover
                            : undefined,
                      cursor: 'pointer',
                    }}
                  >
                    <CountryLabel country={country} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Popover.Content>
      </Popover.Root>
    );
  },
);

function filterCountries(inputValue: string) {
  const lowerCasedInputValue = inputValue.toLowerCase();

  return function countryFilter(country: ComboboxOptionProps) {
    return (
      !inputValue ||
      country.label.toLowerCase().includes(lowerCasedInputValue) ||
      country.value.toLowerCase().includes(lowerCasedInputValue)
    );
  };
}

function CountryLabel({
  country,
  showCountryName = true,
}: {
  country: ComboboxOptionProps;
  showCountryName?: boolean;
}) {
  const { disabled, selectedCountry } = useContextOrThrow(
    PhoneInputContext,
    '`CountryLabel` can only be rendered inside `PhoneInput`',
  );

  if (!country) return null;
  const Flag = country.leadIcon ? (country.leadIcon as Flags.FlagComponent) : undefined;

  return (
    <Flex gap={2} align="center">
      {Flag ? (
        <Flex align="center" justify="center" grow={0} shrink={0} basis={24}>
          <Flag width={24} />
        </Flex>
      ) : (
        <InternalCountryFlagPlaceholder value={country.value} />
      )}
      {showCountryName && (
        <Flex.Item grow={1} shrink={0}>
          <Text
            intent={selectedCountry === country.value ? 'primary' : undefined}
            variant="bodyM"
            contrast={disabled ? 'low' : 'high'}
          >
            {country.label}
          </Text>
        </Flex.Item>
      )}
      <Text
        intent={selectedCountry === country.value ? 'primary' : undefined}
        variant="bodyM"
        contrast={showCountryName || disabled ? 'low' : 'high'}
        overflow="nowrap"
      >
        {isSupportedCountry(country.value) &&
          `+ ${getCountryCallingCode(country.value as CountryCode)}`}
      </Text>
    </Flex>
  );
}
